/**
 * Platform adapters.
 *
 * The sync engine, download manager, cache manager and local scheduler are pure TypeScript and
 * talk to the device only through these interfaces. That keeps the reliability-critical logic
 * unit-testable with in-memory fakes, and lets a future Android/Android TV player reuse the
 * same code by supplying its own adapters.
 */

export interface FileStat {
  size: number;
  mtimeMs: number;
}

export interface FileSystemAdapter {
  /** Create a directory and all missing parents. */
  ensureDir(path: string): Promise<void>;
  exists(path: string): Promise<boolean>;
  stat(path: string): Promise<FileStat | null>;
  /** List file names (not full paths) directly inside a directory. */
  listDir(path: string): Promise<string[]>;
  remove(path: string): Promise<void>;
  /** Atomic within a filesystem: used to publish a verified download. */
  rename(from: string, to: string): Promise<void>;
  /** Streaming append; used for resumable downloads. */
  appendStream(path: string, data: AsyncIterable<Uint8Array>): Promise<number>;
  readFile(path: string): Promise<Uint8Array>;
  writeFile(path: string, data: Uint8Array | string): Promise<void>;
  /** sha256 of a file's contents, lower-case hex. */
  hashFile(path: string): Promise<string>;
  /** Free and total bytes on the volume holding `path`. */
  diskSpace(path: string): Promise<{ freeBytes: number; totalBytes: number }>;
  join(...parts: string[]): string;
}

export interface HttpResponse {
  status: number;
  ok: boolean;
  headers: Record<string, string>;
  json<T>(): Promise<T>;
  text(): Promise<string>;
  /** Streaming body for large downloads. */
  body: AsyncIterable<Uint8Array> | null;
}

export interface HttpRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string | Uint8Array;
  /** Milliseconds; the adapter aborts the request when it elapses. */
  timeoutMs?: number;
  stream?: boolean;
}

export interface HttpAdapter {
  request(url: string, options?: HttpRequestOptions): Promise<HttpResponse>;
}

/** Minimal synchronous key/value + record storage, backed by SQLite on real devices. */
export interface DatabaseAdapter {
  getSetting(key: string): string | null;
  setSetting(key: string, value: string): void;
  deleteSetting(key: string): void;

  /** Manifests are stored by state so activation is a single atomic swap. */
  saveManifest(record: StoredManifest): void;
  getManifest(state: ManifestState): StoredManifest | null;
  /** Atomically make `contentVersion` the active manifest. */
  activateManifest(contentVersion: string): void;
  deleteManifest(contentVersion: string): void;
  listManifests(): StoredManifest[];

  upsertCacheEntry(entry: CacheEntry): void;
  getCacheEntry(sha256: string): CacheEntry | null;
  listCacheEntries(): CacheEntry[];
  deleteCacheEntry(sha256: string): void;
  touchCacheEntry(sha256: string, lastUsedAt: number): void;

  enqueueProofOfPlay(entry: ProofOfPlayRecord): void;
  takeProofOfPlay(limit: number): ProofOfPlayRecord[];
  deleteProofOfPlay(eventIds: string[]): void;
  countProofOfPlay(): number;

  enqueueEvent(entry: PlayerEventRecord): void;
  takeEvents(limit: number): PlayerEventRecord[];
  deleteEvents(eventIds: string[]): void;

  transaction<T>(fn: () => T): T;
}

export type ManifestState = 'active' | 'pending';

export interface StoredManifest {
  contentVersion: string;
  state: ManifestState;
  /** The manifest document as received, serialized. */
  document: string;
  savedAt: number;
  activatedAt: number | null;
}

export interface CacheEntry {
  sha256: string;
  mediaId: string;
  path: string;
  sizeBytes: number;
  mimeType: string;
  downloadedAt: number;
  lastUsedAt: number;
  verified: boolean;
}

export interface ProofOfPlayRecord {
  eventId: string;
  mediaId: string | null;
  playlistId: string | null;
  slideshowId: string | null;
  scheduleId: string | null;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  completed: boolean;
}

export interface PlayerEventRecord {
  eventId: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  code: string;
  message: string;
  context: string | null;
  occurredAt: string;
}

export interface ClockAdapter {
  now(): number;
  /** Overridable in tests so timing logic does not need real waiting. */
  sleep(ms: number): Promise<void>;
}

export interface LoggerAdapter {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
}

export const systemClock: ClockAdapter = {
  now: () => Date.now(),
  sleep: (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
};

export const consoleLogger: LoggerAdapter = {
  debug: (m, c) => console.debug(`[player] ${m}`, c ?? ''),
  info: (m, c) => console.info(`[player] ${m}`, c ?? ''),
  warn: (m, c) => console.warn(`[player] ${m}`, c ?? ''),
  error: (m, c) => console.error(`[player] ${m}`, c ?? ''),
};
